/**
 * PDF Viewer Modal with Text Search
 * ==================================
 * Opens source documents to specific pages for transaction verification.
 * 
 * Features:
 * - Opens PDF to specific page
 * - Page navigation (prev/next)
 * - Zoom controls
 * - Download option
 * - Shows transaction context
 * - TEXT SEARCH with highlighting
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Download, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, ExternalLink, Loader2, Search, ArrowUp, ArrowDown } from 'lucide-react';
import api from '../../utils/api';
import { useFocusTrap } from '../../hooks/useFocusTrap';

interface TransactionContext {
  date: string;
  vendor: string;
  amount: number;
  description?: string;
}

interface SearchResult {
  pageNumber: number;
  lineIndex: number;
  text: string;
  matchStart: number;
  matchEnd: number;
}

interface PageText {
  page_number: number;
  line_count: number;
  lines: Array<{ text: string; confidence: number }>;
  full_text: string;
}

interface PDFViewerModalProps {
  isOpen: boolean;
  reportId: string;
  sourceFile: string;
  sourcePage: number;
  transactionContext?: TransactionContext;
  onClose: () => void;
}

export default function PDFViewerModal({
  isOpen,
  reportId,
  sourceFile,
  sourcePage,
  transactionContext,
  onClose
}: PDFViewerModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalRef, isOpen);

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(sourcePage || 1);
  const [totalPages, setTotalPages] = useState(1);
  const [zoom, setZoom] = useState(100);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [currentResultIndex, setCurrentResultIndex] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [pdfText, setPdfText] = useState<PageText[]>([]);
  const [showSearchPanel, setShowSearchPanel] = useState(false);

  // Fetch PDF URL when modal opens
  useEffect(() => {
    if (isOpen && sourceFile && reportId) {
      fetchPdfUrl();
    }
  }, [isOpen, sourceFile, reportId]);

  // Update page when sourcePage changes - but cap at totalPages
  useEffect(() => {
    if (sourcePage && totalPages > 0) {
      setCurrentPage(Math.min(sourcePage, totalPages));
    } else if (sourcePage) {
      setCurrentPage(sourcePage);
    }
  }, [sourcePage, totalPages]);

  // Auto-search for transaction amount when modal opens with context
  useEffect(() => {
    if (isOpen && transactionContext && pdfText.length > 0 && !searchQuery) {
      // Search for the transaction amount to help locate it on the page
      const amount = Math.abs(transactionContext.amount);
      const amountStr = amount.toFixed(2);
      // Try variations: $1,234.56, 1234.56, 1,234.56
      const searchTerm = amountStr.includes(',') ? amountStr : amountStr;
      setSearchQuery(searchTerm);
      setShowSearchPanel(true);
      // Trigger search after a small delay to let state update
      setTimeout(() => {
        const searchInput = document.querySelector('[data-testid="pdf-search-input"]') as HTMLInputElement;
        if (searchInput) {
          searchInput.form?.dispatchEvent(new Event('submit', { bubbles: true }));
        }
      }, 500);
    }
  }, [isOpen, transactionContext, pdfText]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle if typing in search input
      if ((e.target as HTMLElement).tagName === 'INPUT') {
        if (e.key === 'Enter' && searchQuery) {
          e.preventDefault();
          handleSearch();
        }
        return;
      }
      
      if (e.key === 'ArrowLeft') {
        handlePreviousPage();
      } else if (e.key === 'ArrowRight') {
        handleNextPage();
      } else if (e.key === 'Escape') {
        onClose();
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        setShowSearchPanel(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentPage, totalPages, searchQuery]);

  const fetchPdfUrl = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({ filename: sourceFile });
      const response = await api.get(`/api/v1/reconciliation/source-pdf/${reportId}?${params.toString()}`);
      
      // Try presigned URL first (simpler, works in iframe without auth)
      if (response.url) {
        setPdfUrl(response.url);
        setTotalPages(response.total_pages || 1);
      } else {
        throw new Error('No URL returned');
      }
    } catch (err: any) {
      console.error('Error fetching PDF URL:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load document');
    } finally {
      setLoading(false);
    }
  };

  const fetchPdfText = async () => {
    try {
      setIsSearching(true);
      const params = new URLSearchParams({ filename: sourceFile });
      const response = await api.get(`/api/v1/reconciliation/source-pdf-text/${reportId}?${params.toString()}`);
      
      if (response.pages) {
        setPdfText(response.pages);
        return response.pages;
      }
      return [];
    } catch (err) {
      console.error('Error fetching PDF text:', err);
      return [];
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    
    // Fetch text if not already loaded
    let textData = pdfText;
    if (textData.length === 0) {
      textData = await fetchPdfText();
    }

    // Search through all pages
    const results: SearchResult[] = [];
    const query = searchQuery.toLowerCase();

    for (const page of textData) {
      page.lines.forEach((line, lineIndex) => {
        const lineText = line.text.toLowerCase();
        let startIndex = 0;
        let matchIndex;

        while ((matchIndex = lineText.indexOf(query, startIndex)) !== -1) {
          results.push({
            pageNumber: page.page_number,
            lineIndex,
            text: line.text,
            matchStart: matchIndex,
            matchEnd: matchIndex + query.length
          });
          startIndex = matchIndex + 1;
        }
      });
    }

    setSearchResults(results);
    setCurrentResultIndex(0);
    setIsSearching(false);

    // Jump to first result
    if (results.length > 0) {
      setCurrentPage(results[0].pageNumber);
    }
  };

  const goToNextResult = () => {
    if (searchResults.length === 0) return;
    const nextIndex = (currentResultIndex + 1) % searchResults.length;
    setCurrentResultIndex(nextIndex);
    setCurrentPage(searchResults[nextIndex].pageNumber);
  };

  const goToPreviousResult = () => {
    if (searchResults.length === 0) return;
    const prevIndex = (currentResultIndex - 1 + searchResults.length) % searchResults.length;
    setCurrentResultIndex(prevIndex);
    setCurrentPage(searchResults[prevIndex].pageNumber);
  };

  const handlePreviousPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  }, [currentPage]);

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => Math.min(prev + 1, totalPages));
    }
  }, [currentPage, totalPages]);

  const handleZoomIn = () => {
    if (zoom < 200) {
      setZoom(prev => prev + 25);
    }
  };

  const handleZoomOut = () => {
    if (zoom > 50) {
      setZoom(prev => prev - 25);
    }
  };

  const handleDownload = () => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    }
  };

  const handleOpenInNewTab = () => {
    if (pdfUrl) {
      window.open(`${pdfUrl}#page=${currentPage}`, '_blank');
    }
  };

  const handlePageInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const page = parseInt(e.target.value);
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const jumpToTransactionPage = () => {
    setCurrentPage(Math.min(sourcePage, totalPages));
  };

  // Get current page search results for highlighting
  const currentPageResults = searchResults.filter(r => r.pageNumber === currentPage);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
      data-testid="pdf-viewer-modal"
    >
      <div ref={modalRef} role="dialog" aria-modal="true" aria-label={`PDF Viewer - ${sourceFile}`} className="bg-white rounded-lg shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-gray-900 truncate" title={sourceFile}>
              {sourceFile}
            </h2>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-sm text-gray-500">
                Page {currentPage} of {totalPages}
              </span>
              {transactionContext && (
                <div className="flex items-center gap-2 text-xs text-gray-600 bg-blue-50 px-2 py-1 rounded">
                  <span>{transactionContext.date}</span>
                  <span>•</span>
                  <span className="truncate max-w-[150px]">{transactionContext.vendor}</span>
                  <span>•</span>
                  <span className="font-semibold text-green-700">
                    ${transactionContext.amount.toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Search Toggle */}
            <button
              onClick={() => setShowSearchPanel(!showSearchPanel)}
              className={`p-2 rounded-lg ${showSearchPanel ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100 text-gray-600'}`}
              title="Search in PDF (Ctrl+F)"
            >
              <Search size={18} />
            </button>

            {/* Page Navigation */}
            <div className="flex items-center bg-gray-100 rounded-lg">
              <button
                onClick={handlePreviousPage}
                disabled={currentPage <= 1}
                className="p-2 hover:bg-gray-200 rounded-l-lg disabled:opacity-30 disabled:cursor-not-allowed"
                title="Previous Page (←)"
              >
                <ChevronLeft size={18} />
              </button>
              <input
                type="number"
                value={currentPage}
                onChange={handlePageInput}
                min={1}
                max={totalPages}
                className="w-12 text-center text-sm font-medium bg-transparent border-0 focus:ring-0"
              />
              <button
                onClick={handleNextPage}
                disabled={currentPage >= totalPages}
                className="p-2 hover:bg-gray-200 rounded-r-lg disabled:opacity-30 disabled:cursor-not-allowed"
                title="Next Page (→)"
              >
                <ChevronRight size={18} />
              </button>
            </div>

            <div className="w-px h-6 bg-gray-300"></div>

            {/* Zoom Controls */}
            <div className="flex items-center bg-gray-100 rounded-lg">
              <button
                onClick={handleZoomOut}
                disabled={zoom <= 50}
                className="p-2 hover:bg-gray-200 rounded-l-lg disabled:opacity-30"
                title="Zoom Out"
              >
                <ZoomOut size={18} />
              </button>
              <span className="text-sm font-medium w-14 text-center">{zoom}%</span>
              <button
                onClick={handleZoomIn}
                disabled={zoom >= 200}
                className="p-2 hover:bg-gray-200 rounded-r-lg disabled:opacity-30"
                title="Zoom In"
              >
                <ZoomIn size={18} />
              </button>
            </div>

            <div className="w-px h-6 bg-gray-300"></div>

            {/* Action Buttons */}
            <button
              onClick={handleOpenInNewTab}
              disabled={!pdfUrl}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-30"
              title="Open in New Tab"
            >
              <ExternalLink size={18} />
            </button>
            <button
              onClick={handleDownload}
              disabled={!pdfUrl}
              className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-30"
            >
              <Download size={16} />
              Download
            </button>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg ml-2"
              title="Close (Esc)"
              data-testid="pdf-viewer-close"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Search Panel */}
        {showSearchPanel && (
          <div className="flex items-center gap-3 px-4 py-3 bg-yellow-50 border-b border-yellow-200">
            <Search size={18} className="text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search in document..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoFocus
            />
            <button
              onClick={handleSearch}
              disabled={isSearching || !searchQuery.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSearching ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
              Search
            </button>
            
            {searchResults.length > 0 && (
              <div className="flex items-center gap-2 ml-2">
                <span className="text-sm text-gray-600">
                  {currentResultIndex + 1} of {searchResults.length}
                </span>
                <button
                  onClick={goToPreviousResult}
                  className="p-1 hover:bg-yellow-100 rounded"
                  title="Previous result"
                >
                  <ArrowUp size={16} />
                </button>
                <button
                  onClick={goToNextResult}
                  className="p-1 hover:bg-yellow-100 rounded"
                  title="Next result"
                >
                  <ArrowDown size={16} />
                </button>
              </div>
            )}
            
            {searchQuery && searchResults.length === 0 && !isSearching && (
              <span className="text-sm text-red-600">No results found</span>
            )}
            
            <button
              onClick={() => {
                setShowSearchPanel(false);
                setSearchQuery('');
                setSearchResults([]);
              }}
              className="p-1 text-gray-400 hover:text-gray-600"
            >
              <X size={18} />
            </button>
          </div>
        )}

        {/* Search Results Panel (for current page) */}
        {showSearchPanel && currentPageResults.length > 0 && (
          <div className="px-4 py-2 bg-blue-50 border-b border-blue-200 max-h-32 overflow-y-auto">
            <div className="text-xs font-semibold text-blue-700 mb-1">
              {currentPageResults.length} result(s) on this page:
            </div>
            <div className="space-y-1">
              {currentPageResults.slice(0, 5).map((result, idx) => (
                <div key={idx} className="text-sm text-gray-700 bg-white rounded px-2 py-1">
                  ...{result.text.substring(Math.max(0, result.matchStart - 20), result.matchEnd + 30)}...
                </div>
              ))}
              {currentPageResults.length > 5 && (
                <div className="text-xs text-gray-500">
                  +{currentPageResults.length - 5} more results
                </div>
              )}
            </div>
          </div>
        )}

        {/* PDF Viewer Area */}
        <div className="flex-1 overflow-auto bg-gray-200 p-4">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Loader2 className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4" />
                <p className="text-gray-600">Loading document...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full">
              <div className="bg-white rounded-lg shadow p-8 max-w-md text-center">
                <div className="text-red-500 text-5xl mb-4">📄</div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Unable to Load Document</h4>
                <p className="text-sm text-gray-600 mb-4">{error}</p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={fetchPdfUrl}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Try Again
                  </button>
                  <button
                    onClick={onClose}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          ) : pdfUrl ? (
            <div className="flex flex-col items-center h-full w-full">
              {/* Direct PDF embed with page support */}
              <iframe
                src={`${pdfUrl}#page=${currentPage}&view=FitH`}
                className="bg-white shadow-lg rounded w-full h-full border-0"
                style={{
                  minHeight: '600px',
                  maxWidth: '900px'
                }}
                title={`PDF Viewer - ${sourceFile}`}
                key={`pdf-${currentPage}`}
              />
              
              {/* Quick action bar below PDF */}
              <div className="mt-3 flex items-center gap-4 text-sm">
                <span className="text-gray-500">Can't see the PDF?</span>
                <a 
                  href={`${pdfUrl}#page=${currentPage}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-blue-600 hover:underline font-medium"
                >
                  <ExternalLink size={14} />
                  Open in new tab
                </a>
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t bg-gray-50 rounded-b-lg">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <div className="flex items-center gap-3">
              <span>
                Viewing page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
              </span>
              {sourcePage && sourcePage > totalPages && (
                <span className="text-amber-600 bg-amber-50 px-2 py-1 rounded text-xs">
                  ⚠️ Transaction references page {sourcePage}, but document only has {totalPages} pages
                </span>
              )}
              {sourcePage && currentPage !== sourcePage && sourcePage <= totalPages && (
                <button
                  onClick={jumpToTransactionPage}
                  className="text-blue-600 hover:underline"
                >
                  ↩ Jump to transaction (page {sourcePage})
                </button>
              )}
            </div>
            <span className="text-gray-400 hidden sm:inline">
              <kbd className="px-1 py-0.5 bg-gray-200 rounded text-xs mx-1">Ctrl+F</kbd> to search,
              <kbd className="px-1 py-0.5 bg-gray-200 rounded text-xs mx-1">←</kbd> / 
              <kbd className="px-1 py-0.5 bg-gray-200 rounded text-xs mx-1">→</kbd> to navigate, 
              <kbd className="px-1 py-0.5 bg-gray-200 rounded text-xs mx-1">Esc</kbd> to close
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
